/**
 * Homepage questions and answers.
 *
 * This list is DUPLICATED in `services/analysis-svc/src/seo.ts` (`HOME_FAQ`), which renders it into
 * the server-side page and into FAQPage structured data for answer engines. It is restated rather
 * than imported because the browser bundle must not depend on a backend package. A test in
 * analysis-svc (`seo-home.test.ts`) parses this file and fails if the two ever disagree, so the
 * answer a search engine quotes always matches the answer a visitor reads.
 *
 * Brand copy rules apply: no em dashes, no hype adjectives, no competitor is named, and every claim
 * has to be true of what actually ships today.
 */
export interface FaqItem {
  q: string;
  a: string;
}

export const HOME_FAQ: FaqItem[] = [
  {
    q: 'What is MalwareAnalyzer?',
    a: 'MalwareAnalyzer is a public malware analysis and threat intelligence platform. You submit a file, a URL, or a hash, and it returns a verdict backed by multi-engine static scanning, real sandbox execution, extracted indicators, and a threat graph you can pivot through.',
  },
  {
    q: 'Is it free to use?',
    a: 'Yes. Submitting files and URLs for public analysis is unlimited and free, including the full report. Dataset lookups are capped per day, and premium compute such as private analysis, deep URL scans and the AI assistant spends credits. Every account starts with a free credit grant.',
  },
  {
    q: 'Do you actually execute the file, or only scan it?',
    a: 'Both. Every submission runs through a static tier of antivirus, YARA and hash reputation engines, and executable file types are then detonated in an instrumented full-system sandbox that records the process tree, API activity, files written and network traffic. Behaviour is never simulated or inferred: if a sandbox did not capture a trace, the report says so.',
  },
  {
    q: 'Which operating systems can you detonate on?',
    a: 'Windows and Linux samples run in full-system sandboxes with controlled network egress, and Office documents and PDFs are opened in real reader applications on both. Android packages are analysed statically and detonated when the Android tier is attached. There is no macOS sandbox, so Mach-O samples receive static analysis and lightweight emulation only, which the report states plainly.',
  },
  {
    q: 'Can I scan a website instead of a file?',
    a: 'Yes. A URL scan fetches the page through an egress-locked scanner, records the redirect chain, certificate, headers and page structure, then renders it in an isolated browser to capture requests, cookies, scripts and a screenshot. The same detection engines that scan files are run over the page content and over any file the page serves.',
  },
  {
    q: 'Is my submission public?',
    a: 'Anonymous submissions are public, which is how the community corpus stays useful. Signed-in users can submit privately, and a private sample is never published, never indexed and never shown in the public feeds. URLs containing credentials or tokens are redacted automatically and forced private.',
  },
  {
    q: 'How is the verdict decided?',
    a: 'Signals from every tier are fused into one weighted score, and each report lists every signal that contributed with its weight, so a verdict can be audited rather than taken on trust. Engines that did not run are reported as not run, because an absent detection tier is not evidence that a file is safe.',
  },
  {
    q: 'Can I use it from my own tools?',
    a: 'Yes. There is a documented REST API with an OpenAPI specification, a Model Context Protocol server for AI assistants, STIX 2.1 and CSV threat feeds, a TAXII endpoint, and push connectors for common SIEM and SOAR platforms. Reports can also be downloaded as PDF or Word documents.',
  },
  {
    q: 'What does it mean when a report says analysis was degraded?',
    a: 'It means a tier that should have run did not complete, so its section is incomplete. The platform marks these runs rather than presenting reduced coverage as a clean result, and a degraded sample is eligible for automatic re-analysis. A report with no findings and no degradation notice is a real negative result.',
  },
  {
    q: 'Who runs MalwareAnalyzer?',
    a: 'MalwareAnalyzer is a product from Cyble, a US-headquartered threat intelligence company and a Y Combinator W21 company.',
  },
];
